import { GameResult } from '../../types/database'
import { normalizeVatliUrl } from '../../utils/urlNormalizer'
import { API_CONFIG } from '../../config/api.config'

/**
 * Service để lưu kết quả game lên WordPress backend
 */
export class GameResultService {
  // ✅ Use centralized API configuration
  private static API_BASE_URL = API_CONFIG.wordpress.apiUrl

  /**
   * Lưu kết quả game lên server
   */
  static async saveGameResult(gameResult: GameResult): Promise<{
    success: boolean
    id: string
    attempts: number
    rank: number
  }> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/game-results`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: gameResult.id,
          userId: gameResult.userId,
          gameId: gameResult.gameId,
          lessonId: gameResult.lessonId,
          classId: gameResult.classId,
          chapterId: gameResult.chapterId,
          score: gameResult.score,
          maxScore: gameResult.maxScore,
          percentage: gameResult.percentage,
          timeSpent: gameResult.timeSpent,
          won: gameResult.won,
          roomsCompleted: gameResult.roomsCompleted,
          answers: gameResult.answers,
          startedAt: gameResult.startedAt.toISOString(),
          completedAt: gameResult.completedAt.toISOString()
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('✅ Game result saved to WordPress:', data)

      return data
    } catch (error) {
      console.error('❌ Failed to save game result to WordPress:', error)
      throw error
    }
  }

  /**
   * Lấy kết quả game từ server
   */
  static async getGameResults(
    userId: string,
    gameId?: string,
    classId?: string,
    limit?: number
  ): Promise<GameResult[]> {
    try {
      const params = new URLSearchParams({
        userId,
        ...(gameId && { gameId }),
        ...(classId && { classId }),
        ...(limit && { limit: limit.toString() })
      })

      const response = await fetch(`${this.API_BASE_URL}/game-results?${params}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('✅ Loaded game results from WordPress:', data.length)

      // Convert date strings to Date objects
      return data.map((result: any) => ({
        ...result,
        startedAt: new Date(result.startedAt),
        completedAt: new Date(result.completedAt),
        createdAt: new Date(result.createdAt)
      }))
    } catch (error) {
      console.error('❌ Failed to load game results from WordPress:', error)
      return []
    }
  }

  /**
   * Lấy bảng xếp hạng
   */
  static async getGameLeaderboard(
    gameId?: string,
    classId?: string,
    limit?: number,
    sortBy?: 'time' | 'score' | 'recent',
    filterBy?: 'all' | 'won' | 'perfect'
  ): Promise<GameResult[]> {
    try {
      const params = new URLSearchParams({
        ...(gameId && { gameId }),
        ...(classId && { classId }),
        ...(limit && { limit: limit.toString() }),
        ...(sortBy && { sortBy }),
        ...(filterBy && { filterBy })
      })

      const response = await fetch(`${this.API_BASE_URL}/game-leaderboard?${params}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('✅ Loaded game leaderboard from WordPress:', data.length)

      // Convert date strings to Date objects
      return data.map((result: any) => ({
        ...result,
        startedAt: new Date(result.startedAt),
        completedAt: new Date(result.completedAt),
        createdAt: new Date(result.createdAt)
      }))
    } catch (error) {
      console.error('❌ Failed to load game leaderboard from WordPress:', error)
      return []
    }
  }

  /**
   * Sync local results to server
   */
  static async syncGameResults(localResults: GameResult[]): Promise<{
    synced: number
    failed: number
  }> {
    let synced = 0
    let failed = 0

    for (const result of localResults) {
      try {
        await this.saveGameResult(result)
        synced++
      } catch (error) {
        failed++
        console.error(`Failed to sync game result ${result.id}:`, error)
      }
    }

    console.log(`📊 Sync complete: ${synced} synced, ${failed} failed`)

    return { synced, failed }
  }
}

